import { useEffect, useRef } from 'react';

import { COMPLETE, IDLE, TRANSITIONING } from '../state/experienceState';

const ENABLED_ACTION_SELECTOR = 'button:not(:disabled)';

/**
 * Retains keyboard continuity when an experience replaces its prompt content.
 *
 * @param {string} phase - Current finite-experience phase.
 * @returns {React.MutableRefObject<HTMLElement|null>} Ref for the persistent
 *   prompt region that contains the current actionable control.
 * @sideEffects Moves focus without scrolling after a timed transition finishes
 *   or a completed experience is replayed; it never moves focus on initial mount.
 */
export default function useTransitionFocus(phase) {
  const promptRegionRef = useRef(null);
  const previousPhaseRef = useRef(phase);

  useEffect(() => {
    const previousPhase = previousPhaseRef.current;
    previousPhaseRef.current = phase;
    const finishedTimedTransition = (
      previousPhase === TRANSITIONING && phase !== TRANSITIONING
    );
    const replayedCompletedExperience = (
      previousPhase === COMPLETE && phase === IDLE
    );

    if (!finishedTimedTransition && !replayedCompletedExperience) {
      return;
    }

    const promptRegion = promptRegionRef.current;
    const focusTarget = promptRegion?.querySelector(ENABLED_ACTION_SELECTOR)
      || promptRegion;

    focusTarget?.focus({ preventScroll: true });
  }, [phase]);

  return promptRegionRef;
}
